import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@sezane/front-components';
import { useEffect, useReducer, useRef } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input/min';
import { popinOptinConstants, privacyPolicyUrls, termsUrls } from '../../../constants';
import translate from '../../../translate';
import { PopinFormat } from '../../../types';
import { getContrastedColor, getCountryFromDataSite, getCurrentBrand, getRoute, isValidEmail, isWebview } from '../../../utils';
import Dialogs from '../../dialogs/a11y-dialog-component';
import { fetchUserData } from './apiCalls/fetchUserData';
import { subscribe } from './apiCalls/subscribe';
import { CloseButton } from './CloseButton';
import { popinTracking } from './popinTracking';
import { PopinWrapper } from './PopinWrapper';
import { SubscriptionRadioGroup } from './SubscriptionRadioGroup';
import { Title } from './Title';
/**
 * Normalizes a phone number entered by the user to E.164 format.
 *
 * Why not isValidPhoneNumber(input, country)?
 * isValidPhoneNumber accepts national notation including the trunk prefix
 * (e.g. "0606060606" with FR is valid), but we need a clean E.164 value to
 * send to the API — "+330606060606" would be rejected.
 *
 * parsePhoneNumber understands trunk prefixes and always produces a clean E.164:
 *   normalizePhone('0606060606', 'FR') → '+33606060606'  (trunk stripped)
 *   normalizePhone('606060606',  'FR') → '+33606060606'  (unchanged)
 *
 * Returns null if the input is not parseable or not a valid number.
 */
const normalizePhone = (phoneInput, countryISO) => {
    try {
        const parsed = parsePhoneNumber(phoneInput, countryISO);
        return parsed?.isValid() ? (parsed.number ?? null) : null;
    }
    catch {
        return null;
    }
};
const enableSubscriptions = (subscriptions) => {
    if (!subscriptions.some((subscription) => subscription.status === false)) {
        return null;
    }
    return subscriptions.map((subscription) => (subscription.status === false ? { ...subscription, status: true } : subscription));
};
/**
 * Zustand style generic reducer that merges a partial patch into state.
 * - parsedPhone is recomputed automatically when phoneInput or selectedCountryISO changes.
 * - emailError/phoneError are cleared automatically when the user edits the corresponding field.
 */
const reducer = (state, updater) => {
    const patch = updater(state);
    const next = { ...state, ...patch };
    if ('phoneInput' in patch || 'selectedCountryISO' in patch) {
        next.parsedPhone = next.phoneInput ? normalizePhone(next.phoneInput, next.selectedCountryISO) : null;
        next.phoneError = null;
    }
    if ('email' in patch)
        next.emailError = null;
    if ('smsSubscriptions' in patch && !patch.smsSubscriptions?.some((sub) => sub.status)) {
        next.phoneError = null;
    }
    return next;
};
export const PopinOptin = ({ popin }) => {
    // Exclude mobile webviews - MUST be before any hooks
    if (isWebview()) {
        return null;
    }
    const brandName = getCurrentBrand();
    const defaultSubscriptions = [{ marketingBrandCode: brandName, status: true }];
    const [state, dispatch] = useReducer(reducer, {
        email: null,
        phoneInput: '',
        selectedCountryISO: (getCountryFromDataSite()?.toUpperCase() || 'FR'),
        parsedPhone: null,
        emailSubscriptions: defaultSubscriptions,
        smsSubscriptions: defaultSubscriptions,
        isInitialized: false,
        isUserOptinsLoading: true,
        isUserConnected: false,
        errorMessage: null,
        isSubmitSuccessful: null,
        isSubmitting: false,
        emailError: null,
        phoneError: null,
    });
    const fieldVisibilityCache = useRef(new Map());
    const hasInitializedTracking = useRef(false);
    const hasNormalizedVisibleSubscriptions = useRef(false);
    // Dialog ID depends on the format
    const dialogId = popin.format === PopinFormat.OPTIN_CENTERED
        ? popinOptinConstants.dialogOptinCentered.type
        : popinOptinConstants.dialogOptin.type;
    const titleId = popin.format === PopinFormat.OPTIN_CENTERED
        ? popinOptinConstants.dialogOptinCentered.titleId
        : popinOptinConstants.dialogOptin.titleId;
    const descId = popin.format === PopinFormat.OPTIN_CENTERED
        ? popinOptinConstants.dialogOptinCentered.descId
        : popinOptinConstants.dialogOptin.descId;
    useEffect(() => {
        const abortController = new AbortController();
        dispatch(() => ({ isUserOptinsLoading: true, errorMessage: null }));
        const handleSuccess = (data) => {
            // Extract national number from a pre-existing E.164 phone, if any
            const parsedExisting = data.phone ? parsePhoneNumber(data.phone) : null;
            const replaceNullStatusByTrue = (subs) => subs.map((sub) => ({ ...sub, status: sub.status === null ? true : sub.status }));
            dispatch((s) => ({
                email: data.email,
                emailSubscriptions: data.emailSubscriptions == null ? defaultSubscriptions : replaceNullStatusByTrue(data.emailSubscriptions),
                smsSubscriptions: data.smsSubscriptions == null ? defaultSubscriptions : replaceNullStatusByTrue(data.smsSubscriptions),
                selectedCountryISO: parsedExisting?.country ?? s.selectedCountryISO,
                phoneInput: parsedExisting?.nationalNumber ?? '',
                isInitialized: true,
                isUserConnected: true,
                isUserOptinsLoading: false,
            }));
        };
        const handleError = (error) => {
            dispatch(() => ({
                email: null,
                phoneInput: '',
                emailSubscriptions: defaultSubscriptions,
                smsSubscriptions: defaultSubscriptions,
                isInitialized: true,
                isUserConnected: false,
                isUserOptinsLoading: false,
                errorMessage: error,
            }));
        };
        fetchUserData(abortController, translate('front.popin.optin.fetch.user.data.error'), handleSuccess, handleError);
        return () => {
            abortController.abort();
        };
    }, []);
    useEffect(() => {
        if (!state.isSubmitSuccessful) {
            return;
        }
        Dialogs.hide(dialogId);
    }, [dialogId, state.isSubmitSuccessful]);
    useEffect(() => {
        if (!state.isInitialized || hasInitializedTracking.current) {
            return;
        }
        hasInitializedTracking.current = true;
        popinTracking(popin, dialogId, titleId, descId);
    }, [descId, dialogId, popin, titleId, state.isInitialized]);
    const hasActiveEmailSubscription = state.emailSubscriptions.some((sub) => sub.marketingBrandCode === brandName && sub.status === true);
    const isSubmitButtonDisabled = state.isSubmitting ||
        !!state.emailError ||
        !!state.phoneError ||
        (!state.email && !state.phoneInput) ||
        (!state.email && hasActiveEmailSubscription);
    const isFullScreen = popin.format === PopinFormat.OPTIN_CENTERED;
    const closeIconStyle = {
        color: popin.closeCtaColor || popinOptinConstants.closeCtaDefaultColor,
    };
    const computeShouldShowField = (field, brand) => {
        if (!state.isUserConnected) {
            return true;
        }
        const hasValue = field === 'email' ? state.email !== null : state.parsedPhone !== null;
        if (!hasValue) {
            return true;
        }
        const subscriptions = field === 'email' ? state.emailSubscriptions : state.smsSubscriptions;
        if (!subscriptions?.length) {
            return true;
        }
        return subscriptions.some((sub) => sub.marketingBrandCode === brand && sub.status === false);
    };
    const setFieldVisibilityCache = (cacheKey, shouldShow) => {
        fieldVisibilityCache.current.set(cacheKey, shouldShow);
        return shouldShow;
    };
    // Evaluate field visibility only on first render, then cache
    const showField = (field, brand) => {
        const cacheKey = `${field}-${brand}`;
        if (fieldVisibilityCache.current.has(cacheKey)) {
            return fieldVisibilityCache.current.get(cacheKey);
        }
        const shouldShow = computeShouldShowField(field, brand);
        return setFieldVisibilityCache(cacheKey, shouldShow);
    };
    const showEmailField = state.isUserOptinsLoading ? false : showField('email', brandName);
    const showSmsField = state.isUserOptinsLoading ? false : showField('phone', brandName);
    useEffect(() => {
        if (!state.isUserConnected || hasNormalizedVisibleSubscriptions.current) {
            return;
        }
        hasNormalizedVisibleSubscriptions.current = true;
        const nextEmailSubscriptions = showEmailField ? enableSubscriptions(state.emailSubscriptions) : null;
        const nextSmsSubscriptions = showSmsField ? enableSubscriptions(state.smsSubscriptions) : null;
        if (!nextEmailSubscriptions && !nextSmsSubscriptions) {
            return;
        }
        // Business rule ask by Florian Vignon and valided by legal department https://sezane.atlassian.net/browse/SEZANE-7956
        dispatch(() => ({
            ...(nextEmailSubscriptions ? { emailSubscriptions: nextEmailSubscriptions } : {}),
            ...(nextSmsSubscriptions ? { smsSubscriptions: nextSmsSubscriptions } : {}),
        }));
    }, [showEmailField, showSmsField, state.emailSubscriptions, state.isUserConnected, state.smsSubscriptions]);
    if (state.isUserOptinsLoading) {
        return null; // Don't render anything until we know the user's subscription status to avoid flickering fields
    }
    // Don't render if loading or no fields to show
    if (!showEmailField && !showSmsField) {
        return null;
    }
    const mainCss = isFullScreen ? 'max-w-90 px-3  mx-7.5 lg:px-5 py-5 portrait:self-center lg:self-center z-10' : 'relative max-w-85 max-h-[85svh] px-4 py-10 shadow-popin overflow-y-auto';
    const popinStyle = popin.colorPrimary || popin.colorSecondary
        ? {
            ...(popin.colorPrimary ? { backgroundColor: popin.colorPrimary } : {}),
            ...(popin.colorSecondary ? { color: popin.colorSecondary } : {}),
        }
        : undefined;
    const inputStyle = popin.colorSecondary ? { color: getContrastedColor(popin.colorSecondary, popinOptinConstants.defaultTextColor) } : undefined;
    const divClassNames = `${mainCss} ${popin.colorPrimary ? '' : 'bg-stone-50'}`.trim();
    const legalHtml = translate('front.optin.legal', {
        termsAndConditions: `<a class="link link_label" href="${getRoute(termsUrls)}">${translate('front.optin.terms.and.conditions')}</a>`,
        privacyPolicy: `<a class="link link_label" href="${getRoute(privacyPolicyUrls)}">${translate('front.optin.privacy.policy')}</a>`,
    });
    // Construct UserSubscriptionInfo for SubscriptionRadioGroup (display only)
    const subscriptionInfoForDisplay = {
        email: state.email,
        phone: state.parsedPhone,
        emailSubscriptions: state.emailSubscriptions,
        smsSubscriptions: state.smsSubscriptions,
    };
    const updateSubscriptions = (field, status) => {
        if (field !== 'emailSubscriptions' && field !== 'smsSubscriptions')
            return;
        dispatch((s) => {
            const existing = s[field] || [];
            const updated = [
                ...existing.filter((sub) => sub.marketingBrandCode !== brandName),
                { marketingBrandCode: brandName, status },
            ];
            return field === 'emailSubscriptions'
                ? { emailSubscriptions: updated }
                : { smsSubscriptions: updated };
        });
    };
    const handleSubmit = (e) => {
        dispatch(() => ({ emailError: null, phoneError: null }));
        let hasValidationError = false;
        if (state.email) {
            const emailInputEl = e.currentTarget.elements.namedItem('email');
            if (showEmailField && (!emailInputEl.checkValidity() || !isValidEmail(state.email))) {
                dispatch(() => ({ emailError: translate('front.newsletter.subscribe.email_invalid') }));
                hasValidationError = true;
            }
        }
        if (state.phoneInput) {
            if (!state.parsedPhone) {
                dispatch(() => ({ phoneError: translate('front.newsletter.subscribe.email_or_phone_invalid') }));
                hasValidationError = true;
            }
        }
        else if (showSmsField) {
            const smsOptinYes = state.smsSubscriptions.some((sub) => sub.marketingBrandCode === brandName && sub.status === true);
            if (smsOptinYes) {
                dispatch(() => ({ phoneError: translate('front.newsletter.subscribe.email_or_phone_invalid') }));
                hasValidationError = true;
            }
        }
        if (hasValidationError)
            return;
        dispatch(() => ({ isSubmitting: true, errorMessage: null, isSubmitSuccessful: null }));
        const handleSuccess = () => {
            dispatch(() => ({ isSubmitSuccessful: true, isSubmitting: false }));
        };
        const handleError = (error) => {
            dispatch(() => ({ isSubmitSuccessful: false, errorMessage: error, isSubmitting: false }));
        };
        const payload = {
            email: state.email,
            phone: state.parsedPhone,
            emailSubscriptions: state.emailSubscriptions,
            smsSubscriptions: state.smsSubscriptions,
        };
        subscribe(e, payload, handleSuccess, handleError);
    };
    return (_jsx(PopinWrapper, { isFullScreen: isFullScreen, mediaType: popin.mediaType, mediaUrl: popin.mediaUrl, closeButton: closeIconStyle, uid: popin.uid, oncePerSession: popin.oncePerSession, dialogId: dialogId, labelledbyId: titleId, describedbyId: descId, children: _jsxs("div", { className: divClassNames, style: popinStyle, children: [_jsxs("div", { className: "flex flex-col gap-7 mb-2", children: [isFullScreen ? null : _jsx(CloseButton, { closeIconStyle: closeIconStyle, dialogId: dialogId }), _jsx("div", { id: titleId, children: _jsx(Title, { title: popin.title }) }), _jsx("div", { id: descId, className: popin.isFullHtml ? '' : 'px-9 s-wysiwyg -mt-3 text-center text-xs', 
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML: {
                                __html: popin.isFullHtml ? (popin.contentHtml ?? '') : (popin.content ?? ''),
                            } }), _jsxs("form", { className: "form-group gap-y-2", noValidate: true, onSubmit: (e) => {
                                e.preventDefault();
                                handleSubmit(e);
                            }, children: [showEmailField ? (_jsxs("div", { className: "flex flex-col gap-y-1 w-full", children: [_jsx("input", { type: "email", name: "email", className: `w-full h-12.5 p-2.5 pb-2 bg-white border ${state.emailError ? 'border-red-800' : 'border-gray-200'}`, value: state.email || '', placeholder: translate('front.popin.email.placeholder'), "aria-label": translate('front.popin.email.placeholder'), "aria-describedby": state.emailError ? 'popin-email-error' : undefined, onChange: (e) => {
                                                dispatch(() => ({ email: e.target.value }));
                                            }, style: inputStyle }), state.emailError ? (_jsx("p", { id: "popin-email-error", role: "alert", "aria-live": "polite", className: "mt-0 text-xs text-red-800", children: state.emailError })) : null] })) : null, showSmsField ? (_jsxs("div", { className: "flex flex-col gap-y-1 w-full", children: [_jsxs("div", { className: `flex h-12.5 bg-white border ${state.phoneError ? 'border-red-800' : 'border-gray-200'} w-full overflow-hidden`, children: [_jsx("label", { htmlFor: "popin-newsletter-phone-code", className: "sr-only", children: translate('front.popin.newsletter.phone.code.label') }), _jsx("select", { id: "popin-newsletter-phone-code", name: "phone_code", value: state.selectedCountryISO, onChange: (e) => {
                                                        dispatch(() => ({ selectedCountryISO: e.target.value }));
                                                    }, "aria-label": translate('front.popin.newsletter.phone.code.label'), autoComplete: "tel-country-code", className: "pr-5 mx-2.5 text-gray-950 appearance-none shrink-0 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20aria-hidden%3D%22true%22%20focusable%3D%22false%22%3E%3Cpath%20d%3D%22M16.2182%209L11.9652%2013.2529L7.78184%209.06956L7.70235%209.13911L7.63279%209.20867L6.63911%2010.2123L6.56956%2010.2818L6.5%2010.3514L11.8957%2015.7471L11.9652%2015.8166L17.4304%2010.3514L17.5%2010.2818L16.2182%209Z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_center] bg-[length:1.6rem]", style: inputStyle, children: getCountries().map((country) => (_jsxs("option", { value: country, style: inputStyle, children: ["+", getCountryCallingCode(country)] }, country))) }), _jsx("label", { htmlFor: "popin-newsletter-phone", className: "sr-only", children: translate('front.popin.newsletter.phone.placeholder') }), _jsx("input", { id: "popin-newsletter-phone", type: "tel", name: "phone", autoComplete: "tel-national", className: "flex-1 px-2.5 border-l border-gray-200", placeholder: translate('front.popin.newsletter.phone.placeholder'), value: state.phoneInput, onChange: (e) => {
                                                        dispatch(() => ({ phoneInput: e.target.value }));
                                                    }, style: inputStyle })] }), state.phoneError ? (_jsx("p", { id: "popin-phone-error", role: "alert", "aria-live": "polite", className: "mt-0 text-xs text-red-800", children: state.phoneError })) : null] })) : null, _jsxs("div", { className: "flex flex-col gap-y-4 pt-5", children: [showEmailField ? (_jsx(SubscriptionRadioGroup, { type: "email", brandName: brandName, userSubscriptionInfo: subscriptionInfoForDisplay, onUpdate: updateSubscriptions, textColor: popin.colorSecondary })) : null, showSmsField ? (_jsx(SubscriptionRadioGroup, { type: "sms", brandName: brandName, userSubscriptionInfo: subscriptionInfoForDisplay, onUpdate: updateSubscriptions, textColor: popin.colorSecondary })) : null, state.isSubmitSuccessful === false && state.errorMessage ? (_jsx("div", { id: "newsletter-subscribe-status", role: "alert", "aria-atomic": "true", className: "text-red-900", children: _jsx("p", { children: translate('front.popin.optin.error') }) })) : null, _jsx(Button, { type: "submit", className: "mt-2 text-sm font-medium", disabled: isSubmitButtonDisabled, children: translate('front.popin.optin.submit') })] })] })] }), _jsx("p", { className: "mt-0 mb-4 text-center text-2xs", children: translate('front.popin.optin.mandatory.fields') }), _jsx("p", { className: "mt-0 text-center text-2xs", dangerouslySetInnerHTML: { __html: legalHtml } })] }) }));
};
